import { zodResolver } from '@hookform/resolvers/zod'
import { Mail } from 'lucide-react'
import { useForm } from 'react-hook-form'
import type { Path } from 'react-hook-form'
import { Button, PasswordField, TextField } from '@shared/design-system/ui'
import { AppError } from '@shared/api/error'
import { useSession } from '@shared/lib/auth/session-store'
import { toast } from '@shared/lib/toast/toast-store'
import { applyApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { LoginInputSchema } from '../api/auth.contract'
import { useLoginMutation } from '../api/auth.mutations'
import type { LoginInput } from '../api/auth.contract'
import { getRedirectTarget, navigateTo } from './auth-routing'
import { actions, form } from './auth.css'
import { AuthLayout } from './AuthLayout'

function isInvalidCredentialsError(error: unknown) {
  return (
    error instanceof AppError &&
    error.status === 401 &&
    (error.code === 'InvalidCredentialsError' ||
      error.code === 'InvalidCredentials' ||
      error.code === 'InvalidStaffCredentialsError')
  )
}

export function LoginPage() {
  const setSession = useSession((state) => state.setSession)
  const loginMutation = useLoginMutation()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginInputSchema),
    defaultValues: { email: '', password: '' },
  })
  const hasInvalidCredentialsError =
    errors.email?.type === 'credentials' || errors.password?.type === 'credentials'
  const emailError = errors.email?.type === 'credentials' ? undefined : errors.email?.message

  const onSubmit = handleSubmit(async (values) => {
    try {
      const response = await loginMutation.mutateAsync(values)
      setSession(response.token, response.user)
      toast.success('Sessão iniciada.')
      navigateTo(getRedirectTarget('/'))
    } catch (error) {
      const hasFieldErrors = applyApiFieldErrors(error, (field, fieldError) =>
        setError(field as Path<LoginInput>, fieldError),
      )
      if (hasFieldErrors) {
        toast.error({
          title: 'Revise os campos destacados',
          description: 'Confira os dados informados e tente novamente.',
        })
        return
      }

      if (isInvalidCredentialsError(error)) {
        setError('email', { type: 'credentials' })
        setError('password', { type: 'credentials', message: 'E-mail ou senha inválidos.' })
        toast.error({
          title: 'Revise os campos destacados',
          description: 'Confira o e-mail e a senha informados.',
        })
        return
      }

      toast.error(
        getUserFacingError(
          error,
          error instanceof AppError && error.status === 429
            ? {}
            : {
                title: 'Não foi possível entrar',
                fallback: 'Tente novamente em alguns instantes.',
              },
        ),
      )
    }
  })

  return (
    <AuthLayout
      title="Gestão VendePay"
      subtitle="Acesse o painel interno para acompanhar vendas, finanças, assinaturas e produtos."
    >
      <form data-testid="login-form" className={form} onSubmit={(event) => void onSubmit(event)}>
        <TextField
          label="E-mail"
          data-testid="login-email"
          type="email"
          autoComplete="email"
          leftIcon={<Mail size={17} />}
          status={hasInvalidCredentialsError ? 'error' : undefined}
          error={emailError}
          {...register('email')}
        />
        <PasswordField
          label="Senha"
          data-testid="login-password"
          autoComplete="current-password"
          status={hasInvalidCredentialsError ? 'error' : undefined}
          error={errors.password?.message}
          {...register('password')}
        />
        <div className={actions}>
          <Button
            data-testid="login-submit-btn"
            type="submit"
            fullWidth
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? 'Entrando...' : 'Entrar'}
          </Button>
        </div>
      </form>
    </AuthLayout>
  )
}
